import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, ChevronRight, ClipboardList, MapPinned } from "lucide-react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { isResortOn, selectedResort, setResortIds, toggleResort, usePoll, useResortVersion } from "@/lib/live";
import { fmtDate, rupiah, todayStr } from "@/lib/format";

const greet = () => {
  const h = Number(new Date().toLocaleString("en-US", { timeZone: "Asia/Jakarta", hour: "numeric", hourCycle: "h23" })) % 24;
  return h < 4 || h >= 18 ? "Selamat Malam" : h < 11 ? "Selamat Pagi" : h < 15 ? "Selamat Siang" : "Selamat Sore";
};

const Tile = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl bg-black/25 px-3 py-3">
    <p className="text-[11px] font-medium text-white/75 flex items-center justify-center gap-1"><Icon className="h-3.5 w-3.5" />{label}</p>
    <p className="mt-1 text-sm sm:text-base font-bold tabular-nums break-words" data-testid={testId}>{rupiah(value)}</p>
  </div>
);

const FinanceCard = ({ ang, pin, name, active, total }) => (
  <div className="relative overflow-hidden rounded-[28px] bg-[hsl(var(--primary))] text-primary-foreground px-5 sm:px-8 pt-6 pb-5 sm:py-7 shadow-[0_18px_40px_-18px_hsl(var(--primary)/0.7)] text-center" data-testid="petugas-finance-card">
    <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 sm:h-72 sm:w-72 rounded-full bg-white/10" />
    <div className="pointer-events-none absolute -left-10 -bottom-20 h-40 w-40 sm:h-60 sm:w-60 rounded-full bg-black/10" />
    <div className="relative grid gap-4 lg:grid-cols-[1fr_1.2fr_1.3fr] lg:items-center lg:gap-8">
      <div className="lg:text-left lg:flex lg:items-center lg:gap-4">
        <span className="mx-auto lg:mx-0 h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-white/20 grid place-items-center font-heading text-lg sm:text-xl font-bold shrink-0">{name[0]}</span>
        <div className="min-w-0">
          <p className="mt-2 lg:mt-0 text-sm text-white/85" data-testid="petugas-greeting">{greet()}</p>
          <p className="font-heading text-lg sm:text-xl font-bold leading-tight truncate" data-testid="petugas-name">{name}</p>
          <p className="mt-1 text-[11px] sm:text-xs text-white/70">{fmtDate(todayStr())} · {active}/{total} resort aktif</p>
        </div>
      </div>
      <div className="border-t border-white/20 pt-4 lg:border-t-0 lg:pt-0 lg:border-x lg:px-6">
        <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.14em] text-white/75">Saldo Bersih Hari Ini</p>
        <p className="mt-1 font-heading text-[28px] sm:text-4xl leading-none font-bold tabular-nums break-words" data-testid="petugas-total-terkumpul">{rupiah(ang - pin)}</p>
      </div>
      <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
        <Tile icon={ArrowDownLeft} label="Total Angsuran" value={ang} testId="petugas-total-angsuran" />
        <Tile icon={ArrowUpRight} label="Total Pinjaman" value={pin} testId="petugas-total-pinjaman" />
      </div>
    </div>
  </div>
);

const ResortCard = ({ r, on, picked, onToggle }) => (
  <div className={`relative rounded-3xl border bg-card p-4 shadow-sm transition-opacity ${on ? "" : "opacity-55"} ${picked ? "ring-2 ring-primary" : ""}`} data-testid={`petugas-resort-${r.id}`}>
    <div className="absolute right-4 top-4"><Switch checked={picked} onCheckedChange={onToggle} data-testid={`resort-toggle-${r.id}`} aria-label="Aktifkan resort" /></div>
    <div className="text-center pt-1">
      <span className="mx-auto h-11 w-11 rounded-2xl bg-primary/12 text-primary grid place-items-center"><MapPinned className="h-5 w-5" /></span>
      <p className="mt-2 font-heading font-semibold truncate px-10" data-testid={`resort-name-${r.id}`}>{r.name}</p>
      <span className={`inline-block mt-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${on ? "bg-primary/12 text-primary" : "bg-muted text-muted-foreground"}`}>{picked ? "Aktif" : on ? "Ditampilkan" : "Nonaktif"}</span>
    </div>
    <div className="grid grid-cols-2 gap-2 mt-4 text-center">
      <div className="rounded-2xl bg-muted/60 py-2.5"><p className="text-[11px] text-muted-foreground">Angsuran</p><p className="text-sm font-bold tabular-nums">{rupiah(r.angsuran)}</p></div>
      <div className="rounded-2xl bg-muted/60 py-2.5"><p className="text-[11px] text-muted-foreground">Pinjaman</p><p className="text-sm font-bold tabular-nums">{rupiah(r.pinjaman)}</p></div>
    </div>
    <div className="grid grid-cols-2 gap-2 mt-3">
      <Link to={`/resort/${r.id}`} className="flex items-center justify-center gap-1 rounded-full border h-10 text-sm font-medium hover:bg-muted transition-colors" data-testid={`resort-detail-${r.id}`}>Rincian<ChevronRight className="h-4 w-4" /></Link>
      <Link to={`/resort/${r.id}/tagihan`} className="flex items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground h-10 text-sm font-medium hover:opacity-90 transition-opacity" data-testid={`resort-tagihan-${r.id}`}><ClipboardList className="h-4 w-4" />Tagihan</Link>
    </div>
  </div>
);

export const PetugasHome = ({ name }) => {
  const [d, setD] = useState(null);
  const v = useResortVersion();
  const load = useCallback(() => { api.get("/petugas/today").then((r) => { setResortIds(r.data.resorts.map((x) => x.id)); setD(r.data); }).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  usePoll(load);
  if (!d) return <p className="text-muted-foreground text-center py-10">Memuat...</p>;
  const act = d.resorts.filter((r) => isResortOn(r.id));
  const ang = act.reduce((s, r) => s + r.angsuran, 0);
  const pin = act.reduce((s, r) => s + r.pinjaman, 0);
  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="petugas-home" key={v}>
      <FinanceCard ang={ang} pin={pin} name={name} active={act.length} total={d.resorts.length} />
      <div>
        <p className="text-center font-heading text-base font-semibold">Resort Saya</p>
        <p className="text-center text-xs text-muted-foreground mb-3" data-testid="resort-pick-hint">{selectedResort() ? "Hanya resort aktif yang ditampilkan. Matikan untuk melihat semua resort." : "Semua resort ditampilkan. Aktifkan satu resort untuk fokus."}</p>
        {d.resorts.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{d.resorts.map((r) => <ResortCard key={r.id} r={r} on={isResortOn(r.id)} picked={selectedResort() === r.id} onToggle={() => toggleResort(r.id)} />)}</div>
        ) : <p className="text-sm text-muted-foreground text-center" data-testid="petugas-no-resort">Belum ada resort yang ditugaskan.</p>}
      </div>
    </div>
  );
};
